import { Component, ViewChild, ViewEncapsulation } from '@angular/core';
import { PageEvent } from '@angular/material/paginator';

import { downloadConfig } from '../../configs/dashboard-table.config'
import { HubioTableComponent, HubioTableOptions } from '@hubio/hubio-common-components';
import { DashboardService } from '../../services/dashboard.service';
import { FilterService } from '../../services/filter.service';
import { MatExpansionPanel } from '@angular/material/expansion';
import { TaskRecord } from '../../models/dashboard.model';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { $Locale } from '@hubio/hubio-common-components';
import { StringUtilService } from '@core/services/string-util.service';
import { CLICKABLE_TASKS } from '../../configs/clickable-rows.config';

@Component({
  selector: 'app-communic8-download',
  templateUrl: './communic8-download.component.html',
  styleUrls: ['./communic8-download.component.scss']
})
export class Communic8DownloadComponent {
  @ViewChild('table', { static: true }) public table: HubioTableComponent;

  public actionsConfig: any;
  public tableOptions: HubioTableOptions;
  private tableConfig;
  private downloadConfig: any;
  public data: TaskRecord[];
  private subs: Subscription[] = [];

  constructor(
    private dashboardService: DashboardService,
    public filterService: FilterService,
    private router: Router,
    public $Locale: $Locale,
    private stringUtilService: StringUtilService
  ) {
    this.initMetadata();
    this.actionsConfig = [];
    this.initComponent();
  }

  initMetadata() {
    this.downloadConfig = downloadConfig;
    this.tableConfig = downloadConfig.tableDefinition;
  }

  handleRowClick(task: TaskRecord) {
    const { state } = task;
    if (!CLICKABLE_TASKS[state]) return;
    this.router.navigate(["communic8/file/", task.tseqno]);
  }

  private initComponent() {
    this.initTableOptions();
    this.subs.push(
      this.filterService.events.subscribe((event) => {
        if (this.table) {
          this.resetTable()
        }
        this.filterService.handleAppliedFiltersState(event.filter)
        this.getTasks(event.filter, 1)
      })
    )
  }

  private initTableOptions() {
    this.tableOptions = {
      columns: this.tableConfig.columns,
      tooltip: this.tableConfig.tooltip,
      paginator: {
        paginate: true,
        pageSize: this.dashboardService.PAGE_SIZE,
        pageSizeOptions: this.dashboardService.PAGE_SIZE_OPTIONS
      },
      cache: {
        cache: true,
        maxCacheSize: this.downloadConfig.maxPagesCached
      }
    };
  }

  private resetTable() {
    this.table.resetCache();
    this.table.paginator.firstPage();
    this.tableOptions.paginator.length = 0
  }

  private buildTaskBody(filters: object, pageNum: number) {
    const body = {
      query: {
        ...filters,
      },
      options: {
        pageSize: this.table?.paginator?.pageSize ?? this.dashboardService.PAGE_SIZE
      }
    }

    if (pageNum > 1 && this.filterService.lastKey) {
      body.options['lastKey'] = this.filterService.lastKey
    }
    return body;
  }

  ngAfterViewInit(): void {
    this.subs.push(
      this.table?.paginator.page.subscribe((pageEvent) => {
        const pageChanged = pageEvent.previousPageIndex < pageEvent.pageIndex || pageEvent.pageIndex < pageEvent.previousPageIndex 
        const pageSizeChange = pageEvent.pageSize !== this.dashboardService.previousPageSize;

        if (pageSizeChange) {
          this.dashboardService.previousPageSize = pageEvent.pageSize;
        } else if (pageChanged) return
  
        this.getTasks(this.filterService.lastAppliedFilters, 1, true)
      })
    )
  }

  getTasks(filters: object, pageNum?: number, refresh: boolean = false) {
    
    if (refresh && this.table) {
      this.resetTable()
    }
    const body = this.buildTaskBody(filters, pageNum)

    this.dashboardService.searchTasks(body).toPromise().then((res) => {
      if (!res) {
        this.data = []
        return
      };

      const { results, lastKey } = res;
      this.data = this._parseData(results);

      this.filterService.updateLastKeyState(lastKey ?? null)
      this.table?.setData(this.data, pageNum - 1);

      //Adding 1 to enable the next button (for when data length is the size of page)
      this.tableOptions.paginator.length = (this.data.length + 1) 
        + (this.tableOptions.paginator.length ?? 0); 
    })
  }

  handlePaginationEvent(event: PageEvent) {
    if (event.pageIndex > 0) {
      this.getTasks(this.filterService.lastAppliedFilters, event.pageIndex + 1);
    }
  }

  get hasData() {
    return this.data && (Array.isArray(this.data) && this.data.length)
  }

  ngOnDestroy(): void {
    this.subs.forEach(sub => sub.unsubscribe())
    this.filterService.saveFilters()
  }

  _parseData(data: TaskRecord[]): TaskRecord[] {
    const dateColumns = this.tableOptions.columns.filter((columnConfig) => {
      return columnConfig['type'] == 'date';
    });

    data.forEach((task: TaskRecord) => {
      dateColumns.forEach((columnDef) => {
        if (task[columnDef.displayField] != null) {
          task[columnDef.displayField] = this.stringUtilService.formatDate(new Date(task[columnDef.displayField]).getTime());
        }
      });
    });

    return data;
  }
}